import './App.css';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useHashRoute } from './routing/useHashRoute';
import { api } from './api/apiClient';
import { clearDemoSession, isDemoActive, setDemoSession } from './api/demoSession';
import LoginPage from './pages/LoginPage';
import AppShell from './layout/AppShell';
import DashboardPage from './pages/DashboardPage';
import PlaceholderPage from './pages/PlaceholderPage';
import { useTheme } from './ui/useTheme';
import TeamPage from './pages/TeamPage';
import AdminClientsPage from './pages/AdminClientsPage';
import AdminSitesPage from './pages/AdminSitesPage';
import AdminEmployeesPage from './pages/AdminEmployeesPage';
import AdminLogsPage from './pages/AdminLogsPage';
import TrafficPage from './pages/TrafficPage';
import ProjectsPage from './pages/ProjectsPage';
import LanguagesPage from './pages/LanguagesPage';
import DatabasesPage from './pages/DatabasesPage';
import ResourcesDrivePage from './pages/ResourcesDrivePage';
import GalleryPage from './pages/GalleryPage';
import SettingsPage from './pages/SettingsPage';
import AccountSettingsPage from './pages/AccountSettingsPage';
import AppointmentsPage from './pages/AppointmentsPage';
import UpdatesPage from './pages/UpdatesPage';
import MenusPage from './pages/MenusPage';
import ScriptsPage from './pages/ScriptsPage';
import PublicAppointmentPage from './pages/PublicAppointmentPage';
import { applyAppearanceToDocument } from './ui/panelAppearance';
import { IconAlertTriangle } from './ui/icons';
import AccountPasswordForm from './ui/AccountPasswordForm';

function App() {
  const { route, navigate } = useHashRoute();
  useTheme();
  const [auth, setAuth] = useState({
    status: 'loading', // loading | anonymous | authenticated
    user: null,
    sites: [],
  });
  const [selectedSiteId, setSelectedSiteId] = useState(null);
  const [modules, setModules] = useState([]);
  const [modulesLoaded, setModulesLoaded] = useState(false);
  const [modulesRefreshKey, setModulesRefreshKey] = useState(0);
  const [moduleNotifications, setModuleNotifications] = useState(() => {
    if (typeof window === 'undefined') return {};
    try {
      const raw = window.localStorage.getItem('omniscope:moduleNotifications');
      const parsed = raw ? JSON.parse(raw) : {};
      return parsed && typeof parsed === 'object' ? parsed : {};
    } catch {
      return {};
    }
  });
  const [i18n, setI18n] = useState({ status: 'idle', defaultLang: 'fr', activeLang: 'fr', languages: [] });
  const [unsaved, setUnsaved] = useState({ dirty: false, label: '', onSave: null, onDiscard: null, bump: 0 });
  const [simulation, setSimulation] = useState({
    active: false,
    client: null,
    sites: [],
    selectedSiteId: null,
  });
  const [panelSettings, setPanelSettings] = useState({
    status: 'idle', // idle | loading | ready | error
    userId: null,
    editable: false,
    settings: null,
    error: '',
  });
  const [sessionStatus, setSessionStatus] = useState({
    status: 'idle', // idle | ready
    activeSessions: 1,
    multipleSessions: false,
  });
  const [toast, setToast] = useState(null);
  const lastAppliedAppearanceKey = useRef('');
  const modulePollSnapshotRef = useRef({});

  const routeInfo = useMemo(() => {
    const raw = String(route || '/');
    const parts = raw.split('/').filter(Boolean);
    if (parts.length === 0) return { base: '/', rest: [] };
    return { base: `/${parts[0]}`, rest: parts.slice(1) };
  }, [route]);

  const routeBase = routeInfo.base;
  const routeRest = routeInfo.rest;

  const notificationKey = useCallback((siteId, moduleKey) => {
    return `${Number(siteId) || 0}:${String(moduleKey || '').trim()}`;
  }, []);

  useEffect(() => {
    let cancelled = false;
    api
      .me()
      .then((res) => {
        if (cancelled) return;
        if (!res.ok) {
          if (isDemoActive()) clearDemoSession();
          setAuth({ status: 'anonymous', user: null, sites: [] });
          return;
        }
        setAuth({ status: 'authenticated', user: res.user, sites: res.sites || [] });
      })
      .catch(() => {
        if (cancelled) return;
        setAuth({ status: 'anonymous', user: null, sites: [] });
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const refreshMe = useCallback(async () => {
    const res = await api.me();
    if (!res?.ok) return res;
    setAuth({ status: 'authenticated', user: res.user, sites: res.sites || [] });
    return res;
  }, []);

  useEffect(() => {
    if (auth.status !== 'authenticated') return;
    function onSitesChanged() {
      refreshMe();
    }
    window.addEventListener('omniscope:sitesChanged', onSitesChanged);
    return () => window.removeEventListener('omniscope:sitesChanged', onSitesChanged);
  }, [auth.status, refreshMe]);

  useEffect(() => {
    if (auth.status === 'anonymous' && routeBase !== '/login' && routeBase !== '/appointment') {
      navigate('/login');
    }
  }, [auth.status, routeBase, navigate]);

  useEffect(() => {
    if (auth.status === 'authenticated' && routeBase === '/login') {
      navigate('/dashboard');
    }
  }, [auth.status, routeBase, navigate]);

  useEffect(() => {
    if (auth.status !== 'authenticated') return;
    if (routeBase !== '/appointment') return;
    const token = routeRest?.[0] ? String(routeRest[0]) : '';
    if (!/^[a-f0-9]{32,64}$/i.test(token)) return;
    navigate(`/appointments/open/${token.toLowerCase()}`);
  }, [auth.status, routeBase, routeRest, navigate]);

  useEffect(() => {
    if (auth.status !== 'authenticated') return;
    if (routeBase !== '/settings') return;
    if (String(routeRest?.[0] || '') !== 'account') return;
    navigate('/account');
  }, [auth.status, routeBase, routeRest, navigate]);

  useEffect(() => {
    if (auth.status !== 'authenticated') return;

    const available = auth.sites || [];
    const stored = window.localStorage.getItem('omniscope:selectedSiteId');
    const storedId = stored ? Number(stored) : null;
    const storedOk = storedId && available.some((s) => Number(s.id) === storedId);

    const nextId = storedOk ? storedId : available[0]?.id ?? null;
    setSelectedSiteId(nextId ? Number(nextId) : null);
  }, [auth.status, auth.sites]);

  useEffect(() => {
    if (selectedSiteId == null) return;
    if (simulation.active) return;
    window.localStorage.setItem('omniscope:selectedSiteId', String(selectedSiteId));
  }, [selectedSiteId, simulation.active]);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    try {
      window.localStorage.setItem('omniscope:moduleNotifications', JSON.stringify(moduleNotifications || {}));
    } catch {
      // ignore
    }
  }, [moduleNotifications]);

  useEffect(() => {
    if (!simulation.active) return;
    const id = simulation.selectedSiteId;
    if (id == null) return;
    window.localStorage.setItem('omniscope:simSelectedSiteId', String(id));
  }, [simulation.active, simulation.selectedSiteId]);

  const effectiveSites = useMemo(() => (simulation.active ? simulation.sites : auth.sites || []), [simulation.active, simulation.sites, auth.sites]);
  const effectiveSelectedSiteId = useMemo(
    () => (simulation.active ? simulation.selectedSiteId : selectedSiteId),
    [simulation.active, simulation.selectedSiteId, selectedSiteId]
  );

  useEffect(() => {
    function onModuleEvent(e) {
      const d = e?.detail || {};
      const moduleKey = String(d.moduleKey || '').trim();
      const siteId = Number(d.siteId || 0);
      if (!moduleKey || !Number.isFinite(siteId) || siteId <= 0) return;
      if (
        siteId === Number(effectiveSelectedSiteId || 0) &&
        ((moduleKey === 'appointments' && routeBase === '/appointments') ||
          (moduleKey === 'updates' && routeBase === '/updates'))
      ) {
        return;
      }
      const k = notificationKey(siteId, moduleKey);
      setModuleNotifications((prev) => {
        const base = prev && typeof prev === 'object' ? prev : {};
        const cur = Number(base[k] || 0);
        return { ...base, [k]: Math.max(0, cur + 1) };
      });
    }
    window.addEventListener('omniscope:moduleEvent', onModuleEvent);
    return () => window.removeEventListener('omniscope:moduleEvent', onModuleEvent);
  }, [effectiveSelectedSiteId, routeBase, notificationKey]);

  useEffect(() => {
    const sid = Number(effectiveSelectedSiteId || 0);
    if (!Number.isFinite(sid) || sid <= 0) return;
    const mod =
      routeBase === '/appointments'
        ? 'appointments'
        : routeBase === '/updates'
        ? 'updates'
        : '';
    if (!mod) return;
    const k = notificationKey(sid, mod);
    setModuleNotifications((prev) => {
      const base = prev && typeof prev === 'object' ? prev : {};
      if (!base[k]) return base;
      const next = { ...base };
      delete next[k];
      return next;
    });
  }, [routeBase, effectiveSelectedSiteId, notificationKey]);

  useEffect(() => {
    let cancelled = false;
    if (auth.status !== 'authenticated') return () => {};
    const sid = Number(effectiveSelectedSiteId || 0);
    if (!Number.isFinite(sid) || sid <= 0) return () => {};

    let timerId = null;
    let inflight = false;

    function bumpModule(moduleKey) {
      const key = notificationKey(sid, moduleKey);
      setModuleNotifications((prev) => {
        const base = prev && typeof prev === 'object' ? prev : {};
        const cur = Number(base[key] || 0);
        return { ...base, [key]: Math.max(0, cur + 1) };
      });
    }

    function canNotify(moduleKey) {
      const routeKey =
        routeBase === '/appointments'
          ? 'appointments'
          : routeBase === '/updates'
          ? 'updates'
          : '';
      return routeKey !== moduleKey;
    }

    async function tick() {
      if (cancelled || inflight) return;
      inflight = true;
      try {
        const [appointmentsRes, updatesRes] = await Promise.all([
          api.appointmentsList(sid, { limit: 100 }),
          api.updatesList(sid, { limit: 100 }),
        ]);
        if (cancelled) return;

        if (appointmentsRes?.ok) {
          const list = Array.isArray(appointmentsRes.appointments) ? appointmentsRes.appointments : [];
          const first = list[0] || null;
          const sig = `${list.length}|${Number(first?.id || 0)}|${String(first?.updated_at || first?.start_at || '')}`;
          const snapKey = `${sid}:appointments`;
          const prev = modulePollSnapshotRef.current[snapKey] || '';
          modulePollSnapshotRef.current[snapKey] = sig;
          if (prev && prev !== sig && canNotify('appointments')) bumpModule('appointments');
        }

        if (updatesRes?.ok) {
          const list = Array.isArray(updatesRes.updates) ? updatesRes.updates : [];
          const first = list[0] || null;
          const sig = `${list.length}|${Number(first?.id || 0)}|${String(first?.updated_at || first?.created_at || '')}`;
          const snapKey = `${sid}:updates`;
          const prev = modulePollSnapshotRef.current[snapKey] || '';
          modulePollSnapshotRef.current[snapKey] = sig;
          if (prev && prev !== sig && canNotify('updates')) bumpModule('updates');
        }
      } finally {
        inflight = false;
        if (!cancelled) timerId = window.setTimeout(tick, 10_000);
      }
    }

    tick();
    return () => {
      cancelled = true;
      if (timerId) window.clearTimeout(timerId);
    };
  }, [auth.status, effectiveSelectedSiteId, routeBase, notificationKey]);

  const sidebarNotifications = useMemo(() => {
    const sid = Number(effectiveSelectedSiteId || 0);
    if (!Number.isFinite(sid) || sid <= 0) return {};
    const out = {};
    for (const [k, v] of Object.entries(moduleNotifications || {})) {
      const [rawSid, moduleKey] = String(k || '').split(':');
      if (Number(rawSid) !== sid || !moduleKey) continue;
      const n = Number(v || 0);
      if (Number.isFinite(n) && n > 0) out[moduleKey] = n;
    }
    return out;
  }, [moduleNotifications, effectiveSelectedSiteId]);

  const selectedSite = useMemo(() => {
    const sites = effectiveSites || [];
    return sites.find((s) => Number(s.id) === Number(effectiveSelectedSiteId)) || null;
  }, [effectiveSites, effectiveSelectedSiteId]);

  useEffect(() => {
    if (typeof document === 'undefined') return;
    const appName = 'Omniscope Panel';
    if (auth.status === 'anonymous') {
      document.title = `Connexion — ${appName}`;
      return;
    }
    if (auth.status !== 'authenticated') {
      document.title = appName;
      return;
    }
    const routeLabelMap = {
      '/': 'Dashboard',
      '/dashboard': 'Dashboard',
      '/account': 'Paramètres du compte',
      '/clients': 'Contacts',
      '/sites': 'Sites',
      '/logs': 'Logs',
      '/users': 'Employés',
      '/admin-settings': 'Paramètres admin',
    };
    const fromRoute = routeLabelMap[routeBase] || '';
    const fromModule = fromRoute || (modules || []).find((m) => String(m.route) === String(routeBase))?.label || 'Panel';
    const siteName = String(selectedSite?.name || '').trim();
    document.title = siteName ? `${fromModule} · ${siteName} — ${appName}` : `${fromModule} — ${appName}`;
  }, [auth.status, routeBase, modules, selectedSite?.name]);

  useEffect(() => {
    if (auth.status !== 'authenticated' || effectiveSelectedSiteId == null) {
      setI18n({ status: 'idle', defaultLang: 'fr', activeLang: 'fr', languages: [] });
      return;
    }
    let cancelled = false;
    setI18n((s) => ({ ...s, status: 'loading' }));
    api
      .siteLanguages(Number(effectiveSelectedSiteId))
      .then((res) => {
        if (cancelled) return;
        if (!res.ok) {
          setI18n({ status: 'error', defaultLang: 'fr', activeLang: 'fr', languages: [] });
          return;
        }
        const enabled = (res.languages || []).filter((l) => l.is_enabled);
        const defaultLang = res.default_lang || enabled[0]?.lang_code || 'fr';
        const stored = window.localStorage.getItem(`omniscope:lang:${String(effectiveSelectedSiteId)}`);
        const storedOk = stored && enabled.some((l) => l.lang_code === stored);
        const activeLang = storedOk ? stored : defaultLang;
        setI18n({ status: 'ready', defaultLang, activeLang, languages: res.languages || [] });
      })
      .catch(() => {
        if (cancelled) return;
        setI18n({ status: 'error', defaultLang: 'fr', activeLang: 'fr', languages: [] });
      });
    return () => {
      cancelled = true;
    };
  }, [auth.status, effectiveSelectedSiteId]);

  useEffect(() => {
    if (effectiveSelectedSiteId == null) return;
    if (i18n.status !== 'ready') return;
    window.localStorage.setItem(`omniscope:lang:${String(effectiveSelectedSiteId)}`, String(i18n.activeLang || i18n.defaultLang || 'fr'));
  }, [effectiveSelectedSiteId, i18n.status, i18n.activeLang, i18n.defaultLang]);

  useEffect(() => {
    if (auth.status !== 'authenticated' || effectiveSelectedSiteId == null) {
      setModules([]);
      setModulesLoaded(true);
      return;
    }
    let cancelled = false;
    setModulesLoaded(false);
    api
      .modules(Number(effectiveSelectedSiteId), { as: simulation.active ? 'client' : undefined })
      .then((res) => {
        if (cancelled) return;
        setModules(res.ok ? res.modules || [] : []);
        setModulesLoaded(true);
      })
      .catch(() => {
        if (cancelled) return;
        setModules([]);
        setModulesLoaded(true);
      });
    return () => {
      cancelled = true;
    };
  }, [auth.status, effectiveSelectedSiteId, simulation.active, modulesRefreshKey]);

  const panelSettingsTarget = useMemo(() => {
    const role = String(auth.user?.role || '');
    if (role === 'admin') {
      if (!simulation.active) return { mode: 'self', clientId: null };
      const cid = Number(simulation.client?.id || 0);
      return cid > 0 ? { mode: 'admin', clientId: cid } : { mode: 'none', clientId: null };
    }
    if (role === 'client') return { mode: 'self', clientId: null };
    return { mode: 'none', clientId: null };
  }, [auth.user?.role, simulation.active, simulation.client?.id]);

  useEffect(() => {
    if (auth.status !== 'authenticated' || effectiveSelectedSiteId == null) {
      setPanelSettings({ status: 'idle', clientId: null, editable: false, settings: null, error: '' });
      return;
    }
    if (panelSettingsTarget.mode === 'none') {
      setPanelSettings({ status: 'idle', clientId: null, editable: false, settings: null, error: '' });
      return;
    }
    let cancelled = false;
    setPanelSettings((s) => ({ ...s, status: 'loading', error: '' }));
    api
      .panelSettings(Number(effectiveSelectedSiteId), { client_id: panelSettingsTarget.clientId })
      .then((res) => {
        if (cancelled) return;
        if (!res.ok) {
          setPanelSettings({ status: 'error', clientId: null, editable: false, settings: null, error: res.error || 'Chargement impossible' });
          return;
        }
        setPanelSettings({
          status: 'ready',
          userId: res.user_id ?? null,
          editable: Boolean(res.editable),
          settings: res.settings || null,
          error: '',
        });
      })
      .catch(() => {
        if (cancelled) return;
        setPanelSettings({ status: 'error', clientId: null, editable: false, settings: null, error: 'Chargement impossible' });
      });
    return () => {
      cancelled = true;
    };
  }, [auth.status, effectiveSelectedSiteId, panelSettingsTarget.mode, panelSettingsTarget.clientId]);

  useEffect(() => {
    if (panelSettingsTarget.mode === 'none') return;
    if (panelSettings.status !== 'ready') return;
    const appearance = panelSettings.settings?.appearance || null;
    const key = JSON.stringify({
      mode: panelSettingsTarget.mode,
      userId: panelSettings.userId,
      siteId: effectiveSelectedSiteId,
      appearance,
    });
    if (lastAppliedAppearanceKey.current === key) return;
    lastAppliedAppearanceKey.current = key;
    applyAppearanceToDocument(appearance);
  }, [panelSettingsTarget.mode, panelSettings.status, panelSettings.settings, panelSettings.userId, effectiveSelectedSiteId]);

  const savePanelSettings = useCallback(
    async (nextSettings, { forceReconnect = false } = {}) => {
      if (auth.status !== 'authenticated' || effectiveSelectedSiteId == null) return { ok: false, error: 'Site manquant' };
      if (panelSettingsTarget.mode === 'none') return { ok: false, error: 'Aucun client ciblé' };
      setPanelSettings((s) => ({ ...s, settings: nextSettings, error: '' }));
      const res = await api.panelSettingsUpdate({
        site_id: Number(effectiveSelectedSiteId),
        client_id: panelSettingsTarget.clientId,
        settings: nextSettings,
        force_reconnect: forceReconnect,
      });
      if (!res.ok) {
        setPanelSettings((s) => ({ ...s, error: res.error || 'Enregistrement impossible' }));
        return res;
      }
      return res;
    },
    [auth.status, effectiveSelectedSiteId, panelSettingsTarget.mode, panelSettingsTarget.clientId]
  );

  async function handleLogin({ email, password }) {
    const res = await api.login({ email, password });
    if (!res.ok) return res;
    clearDemoSession();
    setToast(null);
    setAuth({ status: 'authenticated', user: res.user, sites: res.sites || [] });
    navigate('/dashboard');
    return res;
  }

  async function handleStartDemo({ token, demo }) {
    const ok = setDemoSession({ token, demo });
    if (!ok) return { ok: false, error: 'Impossible d’activer la démo' };
    setToast(null);
    const res = await api.me();
    if (!res.ok) {
      clearDemoSession();
      return res;
    }
    setAuth({ status: 'authenticated', user: res.user, sites: res.sites || [] });
    navigate('/dashboard');
    return res;
  }

  async function handleLogout() {
    await api.logout();
    clearDemoSession();
    setSessionStatus({ status: 'idle', activeSessions: 1, multipleSessions: false });
    setAuth({ status: 'anonymous', user: null, sites: [] });
    setSimulation({ active: false, client: null, sites: [], selectedSiteId: null });
    navigate('/login');
  }

  function updateAuthUser(nextUser) {
    if (!nextUser) return;
    setAuth((a) => (a?.status === 'authenticated' ? { ...a, user: nextUser } : a));
  }

  function clientTitle(client) {
    if (!client) return 'Client';
    const company = (client.company_name || '').trim();
    const name = `${(client.first_name || '').trim()} ${(client.last_name || '').trim()}`.trim();
    return company || name || (client.email || '').trim() || `Client #${client.id}`;
  }

  function startSimulation({ client, sites, siteId }) {
    const list = sites || client?.sites || [];
    const stored = window.localStorage.getItem('omniscope:simSelectedSiteId');
    const storedId = stored ? Number(stored) : null;
    const storedOk = storedId && list.some((s) => Number(s.id) === storedId);
    const nextId = siteId ?? (storedOk ? storedId : list[0]?.id ?? null);
    setSimulation({
      active: true,
      client,
      sites: list,
      selectedSiteId: nextId ? Number(nextId) : null,
    });
    navigate('/dashboard');
  }

  function stopSimulation() {
    setSimulation({ active: false, client: null, sites: [], selectedSiteId: null });
    navigate('/clients');
  }

  function bumpUnsaved() {
    setUnsaved((s) => ({ ...s, bump: (s.bump || 0) + 1 }));
  }

  useEffect(() => {
    function onModulesChanged(e) {
      const siteId = Number(e?.detail?.siteId ?? 0);
      if (!siteId) return;
      if (Number(siteId) !== Number(effectiveSelectedSiteId)) return;
      setModulesRefreshKey((k) => k + 1);
    }
    window.addEventListener('omniscope:modulesChanged', onModulesChanged);
    return () => window.removeEventListener('omniscope:modulesChanged', onModulesChanged);
  }, [effectiveSelectedSiteId]);

  useEffect(() => {
    let cancelled = false;
    if (auth.status !== 'authenticated') {
      setSessionStatus({ status: 'idle', activeSessions: 1, multipleSessions: false });
      return () => {};
    }

    let timerId = null;
    let inflight = false;
    let lastTickAt = 0;

    function scheduleNext({ multipleSessions }) {
      if (cancelled) return;
      if (timerId) window.clearTimeout(timerId);
      const delay = multipleSessions ? 6_000 : 20_000;
      timerId = window.setTimeout(() => tickAndSchedule(), delay);
    }

    async function tickAndSchedule() {
      if (cancelled) return;
      if (inflight) return;
      inflight = true;
      lastTickAt = Date.now();
      try {
        const res = await api.sessionStatus();
        if (cancelled) return;
        if (!res.ok) {
          scheduleNext({ multipleSessions: false });
          return;
        }
        const count = Number(res.active_sessions ?? 1);
        const multipleSessions = Boolean(res.multiple_sessions);
        setSessionStatus({
          status: 'ready',
          activeSessions: Number.isFinite(count) && count > 0 ? count : 1,
          multipleSessions,
        });
        scheduleNext({ multipleSessions });
      } finally {
        inflight = false;
      }
    }

    function requestImmediateTick() {
      if (cancelled) return;
      if (Date.now() - lastTickAt < 1_500) return;
      tickAndSchedule();
    }

    function onVis() {
      if (document.visibilityState === 'visible') requestImmediateTick();
    }

    tickAndSchedule();
    window.addEventListener('focus', requestImmediateTick);
    document.addEventListener('visibilitychange', onVis);
    return () => {
      cancelled = true;
      if (timerId) window.clearTimeout(timerId);
      window.removeEventListener('focus', requestImmediateTick);
      document.removeEventListener('visibilitychange', onVis);
    };
  }, [auth.status]);

  useEffect(() => {
    function onUnauthorized(e) {
      if (auth.status !== 'authenticated') return;
      const msg = String(e?.detail?.error || '').toLowerCase().includes('expir')
        ? 'Tu as été déconnecté automatiquement. Reconnecte-toi.'
        : 'Tu as été déconnecté. Reconnecte-toi.';
      setToast({ type: 'danger', message: msg });
      setSessionStatus({ status: 'idle', activeSessions: 1, multipleSessions: false });
      setAuth({ status: 'anonymous', user: null, sites: [] });
      setSimulation({ active: false, client: null, sites: [], selectedSiteId: null });
      navigate('/login');
    }
    window.addEventListener('omniscope:unauthorized', onUnauthorized);
    return () => window.removeEventListener('omniscope:unauthorized', onUnauthorized);
  }, [auth.status, navigate]);

  useEffect(() => {
    if (!simulation.active) return;
    const adminRoutes = ['/clients', '/sites', '/logs', '/admin-settings', '/users'];
    if (adminRoutes.includes(routeBase)) {
      navigate('/dashboard');
    }
  }, [simulation.active, routeBase, navigate]);

  const displayUser = useMemo(() => {
    if (!simulation.active) return auth.user;
    return {
      id: auth.user?.id ?? null,
      email: simulation.client?.email || '',
      display_name: `${clientTitle(simulation.client)} (simulation)`,
      role: 'client',
      simulated: true,
    };
  }, [simulation.active, simulation.client, auth.user]);

  const isSuperadmin = Boolean(auth.user?.role === 'admin' && auth.user?.is_superadmin);
  const mustChangePassword = Boolean(auth.status === 'authenticated' && !isDemoActive() && auth.user?.must_change_password);

  useEffect(() => {
    if (!mustChangePassword) return undefined;
    const body = document.body;
    const prev = body.style.overflow;
    body.style.overflow = 'hidden';
    return () => {
      body.style.overflow = prev;
    };
  }, [mustChangePassword]);

  useEffect(() => {
    if (auth.status !== 'authenticated') return;
    if (!modulesLoaded) return;
    if (routeBase === '/dashboard' || routeBase === '/' || routeBase === '/login') return;
    const alwaysAllowed = ['/appointment', '/account'];
    const alwaysAllowedSuperadmin = ['/clients', '/sites', '/logs', '/users', '/admin-settings'];
    if (alwaysAllowed.includes(routeBase)) return;
    if (isSuperadmin && alwaysAllowedSuperadmin.includes(routeBase)) return;
    const matched = (modules || []).some((m) => m.route === routeBase);
    if (!matched) navigate('/dashboard');
  }, [auth.status, modulesLoaded, modules, routeBase, navigate, isSuperadmin]);

  const toastNode = toast ? (
    <div className={`BottomToast ${toast.type === 'danger' ? 'BottomToast--danger' : ''}`} role="status" aria-live="polite">
      <div className="BottomToastInner">
        <span className="ButtonInline" style={{ gap: 10 }}>
          <IconAlertTriangle />
          <span>{toast.message}</span>
        </span>
        <button type="button" className="Button Button--ghost" onClick={() => setToast(null)}>
          Fermer
        </button>
      </div>
    </div>
  ) : null;

  if (auth.status === 'loading') {
    return (
      <div className="PageCenter">
        <div className="Card" role="status" aria-live="polite">
          Chargement…
        </div>
      </div>
    );
  }

  if (auth.status === 'anonymous') {
    if (routeBase === '/appointment') {
      const token = routeRest?.[0] ? String(routeRest[0]) : '';
      return (
        <>
          {toastNode}
          <PublicAppointmentPage token={token} />
        </>
      );
    }
    return (
      <>
        {toastNode}
        <LoginPage onLogin={handleLogin} onStartDemo={handleStartDemo} />
      </>
    );
  }

  let content = null;
  if (routeBase === '/dashboard' || routeBase === '/') {
    content = <DashboardPage user={displayUser} site={selectedSite} siteId={effectiveSelectedSiteId} modules={modules} onNavigate={navigate} />;
  }
  else {
    const matched = (modules || []).find((m) => m.route === routeBase) || null;
      if (matched) {
        if (matched.key === 'clients') {
          content = <AdminClientsPage onSimulate={startSimulation} />;
        } else if (matched.key === 'sites') {
          content = <AdminSitesPage />;
        } else if (matched.key === 'logs') {
          content = <AdminLogsPage sites={auth.sites || []} />;
        } else if (matched.key === 'traffic') {
          content = <TrafficPage siteId={effectiveSelectedSiteId} site={selectedSite} user={displayUser} panelSettings={panelSettings.settings} />;
      } else if (matched.key === 'languages' || matched.route === '/languages') {
        content = (
          <LanguagesPage
            siteId={effectiveSelectedSiteId}
            site={selectedSite}
            onUnsavedChange={(next) => {
              setUnsaved((s) => ({
                dirty: Boolean(next?.dirty),
                label: next?.label || '',
                onSave: typeof next?.onSave === 'function' ? next.onSave : null,
                onDiscard: typeof next?.onDiscard === 'function' ? next.onDiscard : null,
                bump: s.bump || 0,
              }));
            }}
          />
        );
      } else if (matched.key === 'projects') {
        const deepId = routeRest?.[0] ? Number(routeRest[0]) : null;
        const initialProjectId = Number.isFinite(deepId) && deepId > 0 ? deepId : null;
        content = (
          <ProjectsPage
            siteId={effectiveSelectedSiteId}
            site={selectedSite}
            lang={i18n.activeLang}
            defaultLang={i18n.defaultLang}
            languages={i18n.languages}
            initialProjectId={initialProjectId}
            panelSettings={panelSettings.settings}
            onUnsavedChange={(next) => {
              setUnsaved((s) => ({
                dirty: Boolean(next?.dirty),
                label: next?.label || '',
                onSave: typeof next?.onSave === 'function' ? next.onSave : null,
                onDiscard: typeof next?.onDiscard === 'function' ? next.onDiscard : null,
                bump: s.bump || 0,
              }));
            }}
          />
        );
      } else if (matched.key === 'databases') {
        content = (
          <DatabasesPage
            siteId={effectiveSelectedSiteId}
            site={selectedSite}
            onUnsavedChange={(next) => {
              setUnsaved((s) => ({
                dirty: Boolean(next?.dirty),
                label: next?.label || '',
                onSave: typeof next?.onSave === 'function' ? next.onSave : null,
                onDiscard: typeof next?.onDiscard === 'function' ? next.onDiscard : null,
                bump: s.bump || 0,
              }));
            }}
          />
        );
      } else if (matched.key === 'resources') {
        content = <ResourcesDrivePage siteId={effectiveSelectedSiteId} site={selectedSite} modules={modules} panelSettings={panelSettings.settings} />;
      } else if (matched.key === 'gallery') {
        content = <GalleryPage siteId={effectiveSelectedSiteId} site={selectedSite} modules={modules} />;
      } else if (matched.key === 'settings') {
        content = (
          <SettingsPage
            siteId={effectiveSelectedSiteId}
            site={selectedSite}
            modules={modules}
            routeRest={routeRest}
            onNavigate={navigate}
            user={auth.user}
            simulation={simulation}
            panelSettings={panelSettings}
            onSaveSettings={savePanelSettings}
          />
        );
      } else if (matched.key === 'team') {
        content = <TeamPage siteId={effectiveSelectedSiteId} site={selectedSite} user={displayUser} modules={modules} />;
      } else if (matched.key === 'appointments') {
        const deepToken = routeRest?.[0] === 'open' ? String(routeRest?.[1] || '') : '';
        content = (
          <AppointmentsPage
            siteId={effectiveSelectedSiteId}
            site={selectedSite}
            user={displayUser}
            modules={modules}
            routeRest={routeRest}
            initialPublicToken={deepToken}
            onNavigate={navigate}
          />
        );
      } else if (matched.key === 'updates') {
        content = <UpdatesPage siteId={effectiveSelectedSiteId} site={selectedSite} user={displayUser} modules={modules} />;
      } else if (matched.key === 'menus') {
        content = <MenusPage siteId={effectiveSelectedSiteId} site={selectedSite} />;
      } else if (matched.key === 'scripts') {
        content = <ScriptsPage siteId={effectiveSelectedSiteId} site={selectedSite} />;
      } else if (routeBase === '/appointment') {
        // When authenticated, App redirects this route to the internal module (see effect above).
        content = (
          <div className="PageCenter">
            <div className="Card" role="status" aria-live="polite">
              Redirection…
            </div>
          </div>
        );
      } else {
        content = (
          <PlaceholderPage
            title={matched.label}
            subtitle={matched.coming_soon ? 'À venir' : 'Module en cours de mise en place.'}
          />
        );
      }
      } else if (routeBase === '/logs') {
        content = isSuperadmin ? <AdminLogsPage sites={auth.sites || []} /> : <PlaceholderPage title="Accès refusé" subtitle="Réservé administrateur." />;
      } else if (routeBase === '/users') {
        content = isSuperadmin ? <AdminEmployeesPage sites={auth.sites} /> : <PlaceholderPage title="Accès refusé" subtitle="Réservé administrateur." />;
      } else if (routeBase === '/account') {
      content = <AccountSettingsPage user={auth.user} onUserUpdated={updateAuthUser} panelSettings={panelSettings} onSaveSettings={savePanelSettings} />;
      } else if (routeBase === '/sites') {
        content = <PlaceholderPage title="Sites" subtitle="À venir" />;
      } else {
        content = <PlaceholderPage title="Introuvable" subtitle="La page demandée n’existe pas." />;
      }
  }

  return (
    <>
      {toastNode}
      {mustChangePassword ? (
        <div className="ModalOverlay" role="presentation">
          <div className="Modal" role="dialog" aria-modal="true" aria-label="Mot de passe requis" onMouseDown={(e) => e.stopPropagation()}>
            <div className="ModalHeader">
              <div className="ModalTitle">
                <span className="IconWrap">
                  <IconAlertTriangle />
                </span>
                <div className="TileTitle">Mot de passe temporaire</div>
              </div>
            </div>
            <div className="ModalBody">
              <AccountPasswordForm user={auth.user} mustChange onUserUpdated={updateAuthUser} />
            </div>
          </div>
        </div>
      ) : null}

      <AppShell
        user={displayUser}
        sites={effectiveSites}
        selectedSiteId={effectiveSelectedSiteId}
        onSelectSite={(id) => {
          if (unsaved.dirty && Number(id) !== Number(effectiveSelectedSiteId)) {
            bumpUnsaved();
            return;
          }
          if (simulation.active) setSimulation((s) => ({ ...s, selectedSiteId: Number(id) }));
          else setSelectedSiteId(Number(id));
        }}
        languages={i18n.languages || []}
        selectedLang={i18n.activeLang}
        onSelectLang={(code) => {
          const c = String(code || '').trim();
          if (!c) return;
          if (unsaved.dirty && c !== i18n.activeLang) {
            bumpUnsaved();
            return;
          }
          setI18n((s) => ({ ...s, activeLang: c }));
        }}
        unsaved={unsaved}
        onUnsavedBump={bumpUnsaved}
        modules={modules}
        moduleNotifications={sidebarNotifications}
        route={routeBase}
        onNavigate={navigate}
        onLogout={handleLogout}
        simulation={simulation}
        onExitSimulation={stopSimulation}
        multiSessionWarning={sessionStatus.status === 'ready' && sessionStatus.multipleSessions ? { activeSessions: sessionStatus.activeSessions } : null}
        mobileHeaderSticky={Boolean(panelSettings.settings?.appearance?.mobile_header_sticky)}
      >
        {content}
      </AppShell>
    </>
  );
}

export default App;
